import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ImageWithFallback } from '../components/figma/ImageWithFallback';
import { Calendar, ArrowRight, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getAllPosts, getFeaturedPosts } from '../lib/blog';
import { BlogPostMetadata } from '../types/blog';
import { Reveal } from '../components/Reveal';

export function InsightsPage() {
  const allPosts = getAllPosts();
  const featuredPosts = getFeaturedPosts();

  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#f9fbfc] via-white to-[#F4F7F9] py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl lg:text-6xl text-[#1f2937] mb-6">
              Healthcare <span className="text-[#34AFFB]">Insights</span>
            </h1>
            <p className="text-xl text-[#4B5563]">
              Expert perspectives on the future of care delivery, value-based care strategies, and healthcare innovation.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Featured Articles */}
      {featuredPosts.length > 0 && (
        <>
          <section className="py-8 bg-white border-b border-[#4B5563]/10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-2 text-[#34AFFB]">
                <TrendingUp className="h-5 w-5" />
                <span className="font-medium">Featured Articles</span>
              </div>
            </div>
          </section>

          <section className="py-12 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid lg:grid-cols-2 gap-8">
                {featuredPosts.map((insight, index) => (
                  <Reveal as="div" key={insight.id} delay={(index % 2) * 120} className="h-full">
                    <ArticleCard article={insight} featured />
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* All Articles */}
      <section className="py-16 bg-[#F4F7F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl text-[#1f2937] mb-8">All Articles</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {allPosts.map((insight, index) => (
              <Reveal as="div" key={insight.id} delay={(index % 3) * 110} className="h-full">
                <ArticleCard article={insight} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

interface ArticleCardProps {
  article: BlogPostMetadata;
  featured?: boolean;
}

function ArticleCard({ article, featured = false }: ArticleCardProps) {
  return (
    <Link
      to={`/insights/${article.id}`}
      className="group block h-full"
    >
      <div className={`bg-white rounded-2xl overflow-hidden hover:shadow-xl transition-shadow h-full flex flex-col ${
        featured ? 'bg-[#F4F7F9]' : ''
      }`}>
        <div className={`relative overflow-hidden ${featured ? 'h-64' : 'h-48'}`}>
          <ImageWithFallback
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute top-4 left-4">
            <span className="px-3 py-1 bg-[#34AFFB] text-white rounded-full text-sm">
              {article.category}
            </span>
          </div>
        </div>
        <div className="p-6 flex-1 flex flex-col">
          <div className="flex items-center gap-4 text-sm text-[#4B5563] mb-3">
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {article.date}
            </div>
            <span>•</span>
            <span>{article.readTime}</span>
          </div>
          <h3 className={`${featured ? 'text-2xl' : 'text-xl'} text-[#1f2937] mb-3 group-hover:text-[#34AFFB] transition-colors`}>
            {article.title}
          </h3>
          <p className="text-[#4B5563] mb-4 flex-1">
            {article.excerpt}
          </p>
          {article.tags && article.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {article.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-1 bg-[#F4F7F9] text-[#4B5563] rounded text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 text-[#34AFFB] font-medium">
            Read {featured ? 'More' : 'Article'}
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </Link>
  );
}
